import React, { useEffect } from "react";
import { API_BASE } from "../config";
import { getAccessToken, getStoredUser } from "../api";
import {
  captureFrontendException,
  captureFrontendMessage,
  setMonitoringUser,
} from "../lib/monitoring";

function createTraceId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

async function sendFrontendError(payload) {
  try {
    const token = await getAccessToken();
    await fetch(`${API_BASE}/ops/frontend-error`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Trace-Id": payload.trace_id,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });
  } catch {}
}

function buildPayload(kind, errorLike) {
  const user = getStoredUser();
  setMonitoringUser(user);

  const message =
    typeof errorLike === "string"
      ? errorLike
      : errorLike?.message || "خطأ واجهة غير معروف";

  return {
    trace_id: createTraceId(),
    category: "frontend",
    severity: "high",
    code: kind === "promise" ? "FRONTEND_UNHANDLED_REJECTION" : "FRONTEND_RUNTIME_ERROR",
    message: String(message || "خطأ واجهة غير معروف"),
    page_name: typeof document !== "undefined" ? document.title || "" : "",
    page_path:
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}${window.location.hash}`
        : "",
    operation_name: kind === "promise" ? "unhandledrejection" : "window.onerror",
    file_name: errorLike?.filename || null,
    function_name: null,
    stack: errorLike?.stack || null,
    payload: {
      user_id: user?.id || null,
      user_role: user?.role || null,
    },
  };
}

function reportToSentry(payload) {
  const context = {
    traceId: payload.trace_id,
    tags: {
      code: payload.code,
      category: payload.category,
      severity: payload.severity,
      operation: payload.operation_name,
    },
    extra: {
      page_name: payload.page_name,
      page_path: payload.page_path,
      file_name: payload.file_name,
      stack: payload.stack,
      payload: payload.payload || null,
    },
  };

  if (payload.stack) {
    const error = new Error(payload.message);
    error.name = payload.code || "FrontendRuntimeError";
    error.stack = payload.stack;
    captureFrontendException(error, context);
    return;
  }

  captureFrontendMessage(payload.message, context);
}

export function FrontendErrorMonitor() {
  useEffect(() => {
    setMonitoringUser(getStoredUser());

    const onError = (event) => {
      const payload = buildPayload("error", event?.error || event);
      reportToSentry(payload);
      void sendFrontendError(payload);
    };

    const onUnhandledRejection = (event) => {
      const payload = buildPayload("promise", event?.reason || event);
      reportToSentry(payload);
      void sendFrontendError(payload);
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  return null;
}

export class FrontendErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    const payload = {
      ...buildPayload("error", error),
      code: "FRONTEND_REACT_RENDER_ERROR",
      operation_name: "react.errorBoundary",
      stack: `${error?.stack || ""}\n${info?.componentStack || ""}`.trim(),
    };

    reportToSentry(payload);
    void sendFrontendError(payload);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={fallbackShell}>
          <div style={fallbackCard}>
            <div style={fallbackTitle}>تعذر عرض هذه الصفحة</div>
            <div style={fallbackText}>
              حدث خطأ أثناء تحميل الشاشة الحالية. أعد تحميل الصفحة، وإذا تكرر
              الخلل فارجع للوحة الإدارة ثم افتح الصفحة من جديد.
            </div>
            <div style={fallbackActions}>
              <button
                type="button"
                style={primaryAction}
                onClick={() => window.location.reload()}
              >
                إعادة التحميل
              </button>
              <button
                type="button"
                style={ghostAction}
                onClick={() => {
                  if (window.location.pathname.startsWith("/admin")) {
                    window.location.assign("/admin");
                    return;
                  }

                  window.location.assign("/");
                }}
              >
                صفحة آمنة
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default FrontendErrorMonitor;

const fallbackShell = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
  background: "#f7f9f8",
};

const fallbackCard = {
  width: "100%",
  maxWidth: 520,
  borderRadius: 20,
  border: "1px solid #e4ebe8",
  background: "#ffffff",
  boxShadow: "0 20px 40px rgba(15,23,42,0.08)",
  padding: 24,
  display: "flex",
  flexDirection: "column",
  gap: 14,
  textAlign: "center",
};

const fallbackTitle = {
  color: "#101828",
  fontSize: 24,
  fontWeight: 900,
};

const fallbackText = {
  color: "#475467",
  fontSize: 14,
  lineHeight: 1.9,
};

const fallbackActions = {
  display: "flex",
  justifyContent: "center",
  gap: 10,
  flexWrap: "wrap",
};

const primaryAction = {
  border: "none",
  borderRadius: 14,
  background: "#0aa68c",
  color: "#ffffff",
  padding: "12px 18px",
  fontSize: 14,
  fontWeight: 900,
  cursor: "pointer",
};

const ghostAction = {
  border: "1px solid #d6e0dc",
  borderRadius: 14,
  background: "#ffffff",
  color: "#334155",
  padding: "12px 18px",
  fontSize: 14,
  fontWeight: 800,
  cursor: "pointer",
};
